import { useEffect, useState } from "react";
import type React from "react";
import {
  Alert,
  Center,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  TagsInput,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { Button, Table } from "@xplatform/design-system";
import { IconAlertTriangle, IconPaperclip, IconSend } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { ItemRequestPreviewResponse } from "@models/job";
import { PLACEHOLDER } from "@utils/rowHelpers";
import { normaliseRecipient } from "@utils/recipients";

interface ItemRequestModalProps {
  opened: boolean;
  loading: boolean;
  sending: boolean;
  preview: ItemRequestPreviewResponse | null;
  onClose: () => void;
  onSend: (edited: { subject: string; body: string; to: string[] }) => void;
}

/**
 * The mail a reviewer sends to whoever maintains the item_mp catalogue.
 *
 * Two panes, and the split is the point: the reviewer edits the **note** (a
 * human message about model output, which is exactly what needs softening),
 * while the rows are shown read-only because they travel as a spreadsheet the
 * server builds from the row set. There is nothing to edit there and no way to
 * mangle it — which is the whole reason the table left the mail body.
 */
const ItemRequestModal: React.FC<ItemRequestModalProps> = ({
  opened,
  loading,
  sending,
  preview,
  onClose,
  onSend,
}) => {
  const { t } = useTranslation();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [to, setTo] = useState<string[]>([]);

  // Seed the editable fields once the preview lands; a reviewer who reopens
  // gets the server's wording back rather than their abandoned draft.
  useEffect(() => {
    if (preview) {
      setSubject(preview.subject);
      setBody(preview.body);
      setTo(preview.to);
    }
  }, [preview]);

  // A recipient is now required here rather than left to the server's default:
  // the field is seeded with that default, so an empty box means the reviewer
  // cleared it on purpose, and sending to the configured maintainers anyway
  // would ignore them.
  const canSend =
    !!preview && subject.trim().length > 0 && to.length > 0 && !sending;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="xl"
      title={t("jobResultsPage.itemRequest.title", {
        defaultValue: "Request missing catalogue entries",
      })}
    >
      {loading || !preview ? (
        <Center mih={200}>
          <Loader size="sm" />
        </Center>
      ) : (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {t("jobResultsPage.itemRequest.intro", {
              defaultValue:
                "These components have no Item-MP entry. The reviewer cannot fix that by picking — whoever maintains the catalogue has to create them.",
            })}
          </Text>

          {preview.truncated && (
            <Alert
              color="orange"
              icon={<IconAlertTriangle size={16} />}
              variant="light"
            >
              {t("jobResultsPage.itemRequest.truncated", {
                defaultValue:
                  "This request covers {{shown}} of {{total}} missing entries. Send it, then repeat for the rest.",
                shown: preview.row_count,
                total: preview.total_row_count,
              })}
            </Alert>
          )}

          <TagsInput
            label={t("jobResultsPage.itemRequest.to", { defaultValue: "To" })}
            description={t("jobResultsPage.itemRequest.toHint", {
              defaultValue:
                "Press Enter after each recipient. A name without @ becomes @tecalliance.net.",
            })}
            placeholder={
              to.length === 0
                ? t("jobResultsPage.itemRequest.toPlaceholder", {
                    defaultValue: "huy.pham",
                  })
                : undefined
            }
            value={to}
            onChange={(next) => {
              const cleaned = next.map(normaliseRecipient).filter(Boolean);
              setTo([...new Set(cleaned)]);
            }}
            error={
              to.length === 0
                ? t("jobResultsPage.itemRequest.noRecipients", {
                    defaultValue: "Add at least one recipient.",
                  })
                : undefined
            }
            disabled={sending}
          />

          <TextInput
            label={t("jobResultsPage.itemRequest.subject", {
              defaultValue: "Subject",
            })}
            value={subject}
            onChange={(event) => setSubject(event.currentTarget.value)}
            disabled={sending}
          />

          <Textarea
            label={t("jobResultsPage.itemRequest.body", {
              defaultValue: "Message",
            })}
            description={t("jobResultsPage.itemRequest.bodyHint", {
              defaultValue:
                "Sent as written. The suggested names are model output — reword anything that reads like a decision.",
            })}
            value={body}
            onChange={(event) => setBody(event.currentTarget.value)}
            minRows={7}
            autosize
            maxRows={14}
            disabled={sending}
          />

          <Group gap={6} wrap="nowrap" align="center">
            <IconPaperclip size={15} />
            <Text size="sm">{preview.attachment_filename}</Text>
            <Text size="xs" c="dimmed">
              {t("jobResultsPage.itemRequest.attachmentHint", {
                defaultValue:
                  "{{count}} rows, built from the table below — editing the message does not change it.",
                count: preview.row_count,
              })}
            </Text>
          </Group>

          <ScrollArea.Autosize mah={260} type="auto">
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>#</Table.Th>
                  {/* Same four columns as the result table, so they share its
                      keys — a reviewer reading the request must see the part
                      described the way the table just described it. They were
                      hardcoded English while the column beside them was
                      translated, which also left them untranslated in German. */}
                  <Table.Th>
                    {t("jobResultsPage.wdTable.numberOe", {
                      defaultValue: "OE Number",
                    })}
                  </Table.Th>
                  <Table.Th>
                    {t("jobResultsPage.wdTable.nameOe", {
                      defaultValue: "OE Text",
                    })}
                  </Table.Th>
                  <Table.Th>
                    {t("jobResultsPage.wdTable.oeFunction", {
                      defaultValue: "OE Function",
                    })}
                  </Table.Th>
                  <Table.Th>
                    {t("jobResultsPage.wdTable.oeDomain", {
                      defaultValue: "OE Domain",
                    })}
                  </Table.Th>
                  <Table.Th>
                    {t("jobResultsPage.itemRequest.suggested", {
                      defaultValue: "Suggested ItemMP Text (AI)",
                    })}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {preview.lines.map((line) => (
                  <Table.Tr key={line.row_index}>
                    <Table.Td>
                      <Text size="xs" c="dimmed" ff="monospace">
                        {line.row_index + 1}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" ff="monospace">
                        {line.oe_number ?? PLACEHOLDER}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{line.oe_name ?? PLACEHOLDER}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{line.oe_function ?? PLACEHOLDER}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{line.oe_domain ?? PLACEHOLDER}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" fs="italic">
                        {line.suggested_item_mp_text ?? PLACEHOLDER}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>

          <Group justify="flex-end" gap="xs">
            <Button size="xs" variant="subtle" onClick={onClose} disabled={sending}>
              {t("jobResultsPage.itemRequest.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              size="xs"
              leftSection={<IconSend size={14} />}
              loading={sending}
              disabled={!canSend}
              onClick={() => onSend({ subject: subject.trim(), body, to })}
            >
              {t("jobResultsPage.itemRequest.send", { defaultValue: "Send" })}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
};

export default ItemRequestModal;
